import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faChevronDown,
  faChevronRight,
  faCheck,
  faList,
  faHammer,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import moment from "moment";
import SubtleBadge from "components/common/SubtleBadge";
import { useAppContext } from "providers/AppProvider";
import IconButton from "components/common/IconButton";
import { FaPlus } from "react-icons/fa";
import { getPathFromUrl, getToday, rgbaColor } from "helpers/utils";
import { Briefcase, Building, Eye, Gear, Trash } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  handleAddSplit,
  handleRemoveSplit,
  handleUpdateSplit,
  addValidationError,
  handlePreauthorizationDiagnosis,
  handleAddClaimServices,
  deleteClaimListRow,
  deleteMultipleClaimListRows,
} from "../config/claimSlice";
import { toast } from "react-toastify";
import { Button, Badge } from "react-bootstrap";
import ConfirmationModal from "components/common/custom/confirmation-modal";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import { useForm } from "react-hook-form";

const ExpandedComponent = ({ data }) => {
  const dispatch = useDispatch();
  const { serviceSplits, validationErrors, preauthorizationDiagnosis } =
    useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );
  const { control } = useForm();
  const [localErrors, setLocalErrors] = useState({});

  const splits = serviceSplits[data.preauthfull_id] || [];

  const recalculateRemaining = (service) => {
    const totalUsed = service.splits.reduce(
      (sum, s) => sum + Number(s.quantity || 0),
      0
    );
    const remainingQty = Number(service["approved-quantity"] || 0) - totalUsed;

    return service.splits.map((split, idx) => ({
      ...split,
      remaining: idx === service.splits.length - 1 ? remainingQty : 0,
    }));
  };

  const handleInput = (sIdx, rIdx, field, value) => {
    const service = splits[sIdx];
    const updatedSplits = JSON.parse(JSON.stringify(service.splits)); // Deep copy to avoid reference issues

    const isQuantityChange = field === "quantity";
    let oldLastRemaining;
    const lastIdx = updatedSplits.length - 1;
    if (isQuantityChange) {
      oldLastRemaining = service.splits[lastIdx].remaining;
      const currentRow = updatedSplits[rIdx];
      const maxAllowed = currentRow.quantity + currentRow.remaining;
      value = Math.min(Math.max(0, Number(value)), maxAllowed);
      updatedSplits[rIdx][field] = value;
    } else if (field === "period") {
      updatedSplits[rIdx][field] = {
        start: value[0] ? moment(value[0]).format("YYYY-MM-DD") : getToday(),
        end: value[1]
          ? moment(value[1]).format("YYYY-MM-DD")
          : value[0]
            ? moment(value[0]).format("YYYY-MM-DD")
            : getToday(),
      };
    } else {
      updatedSplits[rIdx][field] = value;
    }

    const updatedService = { ...service, splits: updatedSplits };
    updatedService.splits = recalculateRemaining(updatedService);

    dispatch(
      handleUpdateSplit({
        preauthId: data.preauthfull_id,
        serviceIndex: sIdx,
        split: updatedSplits[rIdx],
      })
    );

    if (isQuantityChange) {
      const newLastRemaining = updatedService.splits[lastIdx].remaining;
      if (oldLastRemaining !== newLastRemaining && rIdx !== lastIdx) {
        dispatch(
          handleUpdateSplit({
            preauthId: data.preauthfull_id,
            serviceIndex: sIdx,
            split: updatedService.splits[lastIdx],
          })
        );
      }
    }

    if (value && (field === "invoice_no" || field === "patient_share")) {
      setLocalErrors((prev) => ({
        ...prev,
        [updatedSplits[rIdx].id]: {
          ...prev[updatedSplits[rIdx].id],
          [field]: undefined,
        },
      }));
    }
  };

  const addSplit = (serviceIndex) => {
    const service = splits[serviceIndex];
    const lastSplit = service.splits[service.splits.length - 1];

    const quantity = Number(lastSplit.quantity);
    const maxAllowed = Number(lastSplit.remaining) + quantity;

    if (!quantity || isNaN(quantity)) {
      toast.error("Quantity is required");
      return;
    } else if (quantity > maxAllowed) {
      toast.error("Quantity exceeds remaining allowed");
      return;
    }

    if (!lastSplit.patient_share?.trim()) {
      toast.error("Patient share is required");
      return;
    }

    if (!lastSplit.invoice_no?.trim()) {
      toast.error("Invoice number is required");
      return;
    }

    const newRemaining = lastSplit.remaining;

    if (newRemaining > 0) {
      const newSplit = {
        id: `${service.prd_code}-${Date.now()}`,
        quantity: newRemaining,
        patient_share: "0",
        invoice_no: "",
        rowNumber: service.splits.length + 1,
        isSplit: true,
        remaining: 0,
        children: [],
        period: {}, // Initialize period as empty
      };

      dispatch(
        handleAddSplit({
          preauthId: data.preauthfull_id,
          serviceIndex,
          split: newSplit,
        })
      );
    } else {
      toast.error("Remaining quantity is 0, cannot split.");
      return;
    }
  };

  const removeSplit = (sIdx, rIdx) => {
    const service = JSON.parse(JSON.stringify(splits[sIdx]));
    const updatedSplits = service.splits.filter((_, idx) => idx !== rIdx);

    if (updatedSplits.length === 0) {
      toast.error("Cannot remove the last split");
      return;
    }
    const updatedService = { ...service, splits: updatedSplits };
    updatedService.splits = recalculateRemaining(updatedService);

    dispatch(
      handleRemoveSplit({
        preauthId: data.preauthfull_id,
        serviceIndex: sIdx,
        splitIndex: rIdx,
      })
    );

    const newLast = updatedService.splits[updatedService.splits.length - 1];

    dispatch(
      handleUpdateSplit({
        preauthId: data.preauthfull_id,
        serviceIndex: sIdx,
        split: newLast,
      })
    );
  };

  const columns = [
    {
      name: "",
      cell: (row) =>
        row.splitIndex === row.splitsLength - 1 && Number(claimsSetting?.add) === 1 && (
          <IconButton
            size="sm"
            variant="outline-success"
            onClick={() => addSplit(row.serviceIndex)}
            title="Add new split"
            disabled={row?.remaining === 0}
          >
            <FaPlus size={12} />
          </IconButton>
        ),
    },
    {
      name: "Service Code",
      selector: (row) => <div className="fs-11">{row.prd_code}</div>,
    },
    {
      name: "Description",
      selector: (row) => (
        <div className="fs-11 text-wrap">{row.prd_descp_txt}</div>
      ),
    },
    {
      name: "Service Period",
      cell: (row, index) => (
        <BootDatePicker
          name={`service_date_${row.id + index}`} // Unique name for each date picker
          isTable={true}
          label=""
          className="border w-200"
          control={control}
          selectsRange
          popperProps={{ strategy: "fixed" }}
          defaultValue={
            row?.period?.start && row?.period?.end
              ? [new Date(row.period.start), new Date(row.period.end)]
              : null // Set to null for empty period
          }
          value={
            row?.period?.start && row?.period?.end
              ? [new Date(row.period.start), new Date(row.period.end)]
              : null // Set to null for empty period
          }
          disabled={Number(claimsSetting?.add) !== 1}
          onChange={(selected) =>
            handleInput(row.serviceIndex, row.splitIndex, "period", selected)
          }
        />
      ),
      minWidth: "300px",
      width: "300px",
    },
    {
      name: "Qty",
      cell: (row) => (
        <div className="">
          <input
            type="number"
            min="0"
            max={row.quantity + row.remaining}
            className={`form-control form-control-sm ${localErrors[row.id]?.quantity ||
              validationErrors[row.id]?.quantity
              ? "is-invalid"
              : ""
              }`}
            disabled={Number(claimsSetting?.add) !== 1}
            value={row.quantity}
            onChange={(e) =>
              handleInput(
                row.serviceIndex,
                row.splitIndex,
                "quantity",
                e.target.value
              )
            }
          />
          {(localErrors[row.id]?.quantity ||
            validationErrors[row.id]?.quantity) && (
              <div className="invalid-feedback">
                {localErrors[row.id]?.quantity ||
                  validationErrors[row.id]?.quantity}
              </div>
            )}
        </div>
      ),
    },
    {
      name: "Rem. Qty",
      cell: (row) => <span className="text-muted">{row.remaining}</span>,
    },
    {
      name: "Unit Price",
      selector: (row) => row.prd_pri,
    },
    {
      name: "Net Amt.",
      selector: (row) =>
        (Number(row.quantity) * Number(row.prd_pri || 0)).toFixed(2),
    },
    {
      name: "Practitioner",
      selector: (row) => row.prov_cde || "N/A",
    },
    {
      name: "Patient Share",
      cell: (row) => (
        <div className="">
          <input
            type="text"
            className={`form-control form-control-sm ${localErrors[row.id]?.patient_share ||
              validationErrors[row.id]?.patient_share
              ? "is-invalid"
              : ""
              }`}
            disabled={Number(claimsSetting?.add) !== 1}
            value={
              row.patient_share != 0 ? row?.patient_share : row?.copay || 0
            }
            onChange={(e) =>
              handleInput(
                row.serviceIndex,
                row.splitIndex,
                "patient_share",
                e.target.value
              )
            }
          />
          {(localErrors[row.id]?.patient_share ||
            validationErrors[row.id]?.patient_share) && (
              <div className="invalid-feedback">
                {localErrors[row.id]?.patient_share ||
                  validationErrors[row.id]?.patient_share}
              </div>
            )}
        </div>
      ),
      width: "150px",
    },
    {
      name: "Invoice No",
      cell: (row) => (
        <div className="">
          <input
            type="text"
            className={`form-control form-control-sm ${localErrors?.[row?.id]?.invoice_no ||
              validationErrors?.[row?.id]?.invoice_no
              ? "is-invalid"
              : ""
              }`}
            disabled={Number(claimsSetting?.add) !== 1}
            value={row.invoice_no}
            onChange={(e) =>
              handleInput(
                row.serviceIndex,
                row.splitIndex,
                "invoice_no",
                e.target.value
              )
            }
          />
          {/* {(localErrors[row.id]?.invoice_no ||
            validationErrors[row.id]?.invoice_no) && (
            <div className="invalid-feedback">
              {localErrors[row.id]?.invoice_no ||
                validationErrors[row.id]?.invoice_no}
            </div>
          )} */}
        </div>
      ),
      width: "150px",
    },
    {
      name: <Gear />,
      cell: (row) =>
        row.isSplit && Number(claimsSetting?.add) === 1 && (
          <IconButton
            size="sm"
            className="btn btn-sm btn-danger"
            onClick={() => removeSplit(row.serviceIndex, row.splitIndex)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </IconButton>
        ),
      width: "80px",
    },
  ];

  const flatData = splits?.flatMap((service, serviceIndex) =>
    service.splits.map((row, rowIndex) => ({
      ...row,
      ...service,
      serviceIndex,
      splitIndex: rowIndex,
      splitsLength: service.splits.length,
    }))
  );

  return (
    <div className="border m-2 border-success rounded">
      <DataTable
        data={flatData}
        columns={columns}
        dense
        noHeader
        responsive
        pagination={false}
      />
    </div>
  );
};

const PrepareClaimTable = ({
  totalRows,
  handlePageChange,
  handlePerRowsChange,
  data,
  page,
  rowsPerPage,
}) => {
  const {
    config: { theme },
    getThemeColor,
  } = useAppContext();
  const dispatch = useDispatch();
  const { preauthorizationDiagnosis } = useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );
  const [expandAll, setExpandAll] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [row, setRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const toggleExpandAll = () => {
    setExpandAll((prev) => !prev);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteClaimListRow({ id: row?.preauthfull_id }));
    setShowDeleteModal(false);
  };

  const handleConfirmBulkDelete = () => {
    const ids = selectedRows.map((row) => row.preauthfull_id);
    dispatch(deleteMultipleClaimListRows({ ids }));
    setSelectedRows([]);
    setToggleCleared(!toggleCleared);
    setShowBulkDeleteModal(false);
    toast.success(`${ids.length} preauthorizations deleted successfully`);
  };

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const contextActions = useMemo(() => {
    const handleBulkDelete = () => {
      if (selectedRows.length === 0) {
        toast.error("Please select at least one row to delete");
        return;
      }
      setShowBulkDeleteModal(true);
    };

    return (
      <div className="d-flex align-items-center gap-2">
        <Badge bg="primary" className="fs-6 p-2">
          {selectedRows.length} selected
        </Badge>
        <Button
          size="sm"
          variant="danger"
          onClick={handleBulkDelete}
          className="d-flex align-items-center gap-1"
          disabled={Number(claimsSetting?.delete) !== 1}
        >
          <FontAwesomeIcon icon={faTrash} />
          Delete Selected
        </Button>
      </div>
    );
  }, [selectedRows]);

  const tableData = useMemo(() => {
    return data?.map((row) => {
      const rowDiagnoses = preauthorizationDiagnosis.filter(
        (diag) =>
          diag.preauthfull_id === (row.preauthfull_id || row.advance_preauthid)
      );
      return {
        ...row,
        diag: rowDiagnoses.length
          ? rowDiagnoses.map((diag) => ({
            diag_code: diag.code,
            diag_typ: diag.description,
          }))
          : row.diag || [],
      };
    });
  }, [data, preauthorizationDiagnosis]);

  const filteredData = tableData?.filter((row) => {
    const items = Array.isArray(row.item)
      ? row.item
      : Object.values(row.item || {});
    const hasValidServiceCode = items.some(
      (item) => item.prd_code && item.prd_code.trim() !== ""
    );
    return hasValidServiceCode;
  });

  const columns = [
    {
      name: "#",
      selector: (row, index) => (
        <div
          className="rounded-circle bg-200 d-flex flex-center fs-11 fw-bold"
          style={{ width: 30, height: 30 }}
        >
          {page * rowsPerPage + index + 1}
        </div>
      ),
      width: "64px",
    },
    {
      name: "Payer",
      selector: (row) => (
        <div className="fs-11 ">
          {row?.insurance_direct === 0
            ? `${row?.payer_name} - ${row?.insurance_shname}`
            : row?.payer_name}
        </div>
      ),
      width: "6rem",
    },
    {
      name: "Preauth ID",
      selector: (row) => (
        <div className="d-flex flex-column">
          <span className="text-success fs-11 fw-medium">
            {row.preauthfull_id}
          </span>
          {row.pauthprior_id && (
            <span className="text-500 fs-11">({row.pauthprior_id})</span>
          )}
        </div>
      ),
      width: "8rem",
    },
    {
      name: "Request Date",
      selector: (row) => (
        <div className="fs-11">
          {moment(row.created_at).format("DD-MM-YYYY , hh:mm:ss A")}
        </div>
      ),
    },
    {
      name: "Patient",
      cell: (row) => (
        <div className="d-flex flex-column ">
          <div className="fs-11 text-700">
            {`${row?.patient_mrn || ""} - ${row?.patient_firstname || ""} ${row?.patient_familyname || ""
              }`}
          </div>
          <div className="text-500 fs-11">
            {row?.patient_identifier ? `${row?.patient_identifier} - ` : ""}{" "}
            {row?.patient_nationalid}
          </div>
        </div>
      ),
    },
    {
      name: "Preauth Ref No",
      selector: (row) => (
        <div className="text-wrap fs-11">{row?.preauthref || ""}</div>
      ),
    },
    {
      name: "PreAuth Type",
      cell: (row) => {
        const typeConfig = {
          institutional: {
            bg: rgbaColor(getThemeColor("primary"), 0.1),
            color: getThemeColor("primary"),
            icon: <Building />,
          },
          dental: {
            bg: rgbaColor(getThemeColor("danger"), 0.1),
            color: getThemeColor("danger"),
            icon: <i className="fas fa-tooth" />,
          },
          pharmacy: {
            bg: rgbaColor(getThemeColor("info"), 0.1),
            color: getThemeColor("info"),
            icon: <i className="fas fa-pills" />,
          },
          professional: {
            bg: rgbaColor(getThemeColor("success"), 0.1),
            color: getThemeColor("success"),
            icon: <Briefcase />,
          },
          vision: {
            bg: rgbaColor(getThemeColor("warning"), 0.1),
            color: getThemeColor("warning"),
            icon: <Eye />,
          },
        };

        const typeKey = row?.preauthclmtyp?.toLowerCase();
        const config = typeConfig[typeKey] || {
          bg: "#f8f9fa",
          color: "#000",
          icon: <i className="fas fa-question-circle me-1" />,
        };

        return (
          <div className="d-flex flex-column justify-content-start ">
            <div className="text-success fw-medium fs-11">
              {row?.preauthclmtyp?.toLowerCase() === "oral"
                ? "Dental"
                : row.preauthclmtyp.charAt(0).toUpperCase() +
                row.preauthclmtyp.slice(1)}
            </div>
            <div className="text-500 fw-normal fs-11">
              {row?.claimsub_types_disp_name &&
                row.claimsub_types_disp_name.charAt(0).toUpperCase() +
                row.claimsub_types_disp_name.slice(1)}
            </div>
          </div>
        );
      },
    },
    {
      name: "Diagnosis",
      cell: (row) => {
        if (!row.diag || row.diag.length === 0) return <span>-</span>;

        const diagArray = Array.isArray(row.diag)
          ? row.diag
          : Object.values(row.diag);
        const diagText = diagArray
          .map(
            (diagnosis) => `(${diagnosis.diag_code}) - ${diagnosis.diag_typ}`
          )
          .join(", ");

        return <span className="fs-11">{diagText || "-"}</span>;
      },
    },
    {
      name: "Status",
      cell: (row) => {
        const value = row.pre_auth_status?.toLowerCase();
        const map = {
          error: { bg: "#fbdbe1", color: "#b82c46", icon: "exclamation" },
          Error: { bg: "#fbdbe1", color: "#b82c46", icon: "exclamation" },
          invalid: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
          "no response": {
            bg: "#08101b",
            color: "#91979e",
            icon: "circle-exclamation",
          },
          approved: { bg: "#d9f8eb", color: "#00894f", icon: "check" },
          valid: { bg: "#e6e8ec", color: "#5d6776", icon: "check" },
          rejected: { bg: "#fbdbe1", color: "#b82c46", icon: "times" },
          cancelled: { bg: "#ffe0fe", color: "#da23aa", icon: "times" },
          partial: {
            bg: "#C2EEF6",
            color: "#0da9bd",
            icon: "star-half-stroke",
          },
          "partially approved": {
            bg: "#C2EEF6",
            color: "#0da9bd",
            icon: "star-half-stroke",
          },
          pended: { bg: "#d9e7fa", color: "#215cac", icon: "clock" },
          "not-required": {
            bg: "#d8f3ff",
            color: "#197aa4",
            icon: "clock",
          },
          queued: { bg: "#fefad2", color: "#a7970d", icon: "list" },
          adjudication: { bg: "#e8dff7", color: "#6f42c1", icon: "gavel" },
          prepared: { bg: "#e6e8ec", color: "#5d6776", icon: "hammer" },
        };
        const config = map[value] || {
          bg: "#eee",
          color: "#000",
          icon: faList,
        };
        return (
          <div className="d-flex justify-content-start ">
            <SubtleBadge
              pill
              style={{
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`,
                width: "120px",
              }}
              className="d-flex flex-center text-capitalize fs-12 p-2 gap-1"
            >
              <FontAwesomeIcon
                icon={config.icon}
                transform="shrink-2"
                className="ms-1 me-1"
              />
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}
            </SubtleBadge>
          </div>
        );
      },
    },
    {
      name: <Gear />,
      sortable: false,
      cell: (row) => (
        <IconButton
          size="sm"
          variant="danger"
          disabled={Number(claimsSetting?.delete) !== 1}
          onClick={() => {
            setRow(row);
            setShowDeleteModal(true);
          }}
        >
          <Trash />
        </IconButton>
      ),
    },
  ];

  const CustomHeader = useMemo(() => {
    return (
      selectedRows?.length > 0 && (
        <div
          style={{ display: `${selectedRows?.length > 0 ? "flex" : "none"}` }}
          className="d-flex  bg-transparent justify-content-between align-items-center p-2 rounded"
        >
          <div className="d-flex align-items-center">
            <span className="fw-medium">
              {selectedRows.length} item{selectedRows.length !== 1 ? "s" : ""}{" "}
              selected
            </span>
          </div>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setShowBulkDeleteModal(true)}
            className="d-flex align-items-center gap-1"
            disabled={Number(claimsSetting?.delete) !== 1}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete Selected
          </Button>
        </div>
      )
    );
  }, [selectedRows]);

  return (
    <>
      <div className="mt-3 rounded">
        {filteredData?.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
            <Button
              size="sm"
              variant="outline-danger"
              onClick={toggleExpandAll}
              className="d-flex fs-11 align-items-center gap-1"
            >
              <FontAwesomeIcon
                icon={expandAll ? faChevronDown : faChevronRight}
              />
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>
        )}
        <div
          className={`${filteredData?.length > 0 && "border-1 border border-primary rounded"
            }`}
        >
          <DataTable
            data={filteredData}
            columns={columns}
            responsive
            theme={theme}
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            expandableRowExpanded={() => expandAll}
            paginationServer
            paginationTotalRows={totalRows}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationPerPage={rowsPerPage}
            selectableRows
            onSelectedRowsChange={handleRowSelected}
            contextActions={contextActions}
            clearSelectedRows={toggleCleared}
            selectableRowsHighlight
            subHeader={selectedRows.length > 0}
            subHeaderComponent={selectedRows.length > 0 ? CustomHeader : null}
          />
        </div>
      </div>
      <ConfirmationModal
        modal={showDeleteModal}
        setModal={setShowDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this preauthorization?"
      />
      <ConfirmationModal
        modal={showBulkDeleteModal}
        setModal={setShowBulkDeleteModal}
        onConfirm={handleConfirmBulkDelete}
        title="Confirm Bulk Delete"
        description={`Are you sure you want to delete ${selectedRows.length} selected preauthorizations? This action cannot be undone.`}
      />
    </>
  );
};

export default PrepareClaimTable;
